import React, { useCallback, useEffect, useState, createElement } from "react";
import { Modal, Pressable, SafeAreaView, StyleSheet, TextInput, View, useWindowDimensions } from "react-native";
import {
  ArrowsInIcon,
  ArrowsOutIcon,
  CaretLeftIcon,
  CaretRightIcon,
  CheckIcon,
  PencilSimpleIcon,
  XIcon,
} from "@phosphor-icons/react";
import { DatePickerInput } from "react-native-paper-dates";

import CustomText from "../../../../../components/common/general/CustomText/CustomText";
import CustomHtml from "../../../../../components/common/general/CustomHtml/CustomHtml";
import { colors } from "../../../../../styles/design-system";
import type { SegmentFieldConfig, SegmentVariant, StructuredFields } from "../types/reviewTypes";
import { AIRewriteActionButton, AIRewriteModal, useAIRewriteSuggestion } from "./AIRewriteControls";
import FullContentEditorModal from "./FullContentEditorModal";

// ---------------------------------------------------------------------------
// Segment configurations per variant
// ---------------------------------------------------------------------------

const ACTIVITY_SEGMENTS: SegmentFieldConfig[] = [
  { key: "title", label: "TITLE", inputType: "text" },
  { key: "description", label: "DESCRIPTION", inputType: "textarea" },
  { key: "dueDate", label: "DUE DATE", inputType: "date" },
  { key: "points", label: "POINTS POSSIBLE", inputType: "number" },
];

const PAGE_SEGMENTS: SegmentFieldConfig[] = [
  { key: "title", label: "TITLE", inputType: "text" },
  { key: "description", label: "PAGE CONTENT", inputType: "textarea" },
];

// Webpage variant shows title as a normal text field but renders the body as
// HTML source in a dedicated code-style textarea.
const WEBPAGE_SEGMENTS: SegmentFieldConfig[] = [
  { key: "title", label: "TITLE", inputType: "text" },
  { key: "description", label: "HTML BODY", inputType: "html" },
];

const LINK_SEGMENTS: SegmentFieldConfig[] = [
  { key: "title", label: "TITLE", inputType: "text" },
  { key: "url", label: "URL", inputType: "url" },
];

const CHECKLIST_SEGMENTS: SegmentFieldConfig[] = [
  { key: "title", label: "TITLE", inputType: "text" },
  { key: "description", label: "DESCRIPTION", inputType: "textarea" },
];

const RECOMMENDATION_SEGMENTS: SegmentFieldConfig[] = [
  { key: "title", label: "TITLE", inputType: "text" },
  { key: "description", label: "DESCRIPTION", inputType: "textarea" },
];

const MODULE_SEGMENTS: SegmentFieldConfig[] = [
  { key: "title", label: "NAME", inputType: "text" },
  { key: "description", label: "DESCRIPTION", inputType: "textarea" },
];

const VARIANT_SEGMENTS: Record<SegmentVariant, SegmentFieldConfig[]> = {
  activity: ACTIVITY_SEGMENTS,
  page: PAGE_SEGMENTS,
  webpage: WEBPAGE_SEGMENTS,
  link: LINK_SEGMENTS,
  checklist: CHECKLIST_SEGMENTS,
  recommendation: RECOMMENDATION_SEGMENTS,
  module: MODULE_SEGMENTS,
};

function parsePointsInput(value: string): number | null | undefined {
  const trimmedValue = value.trim();
  if (!trimmedValue) return null;

  const parsedValue = Number(trimmedValue);
  return Number.isFinite(parsedValue) ? parsedValue : undefined;
}

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface SegmentedDiffViewProps {
  variant: SegmentVariant;
  currentFields: StructuredFields;
  proposedFields: StructuredFields;
  isEditDisabled?: boolean;
  onSaveEdit?: (editedFields: StructuredFields) => void;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

function SegmentedDiffView({
  variant,
  currentFields,
  proposedFields,
  isEditDisabled,
  onSaveEdit,
}: SegmentedDiffViewProps) {
  const { width } = useWindowDimensions();
  const showTwoColumns = width >= 900;
  const allSegments = VARIANT_SEGMENTS[variant];
  const canEdit = Boolean(onSaveEdit) && !isEditDisabled;
  const {
    title: proposedTitle,
    description: proposedDescription,
    dueDate: proposedDueDate,
    points: proposedPoints,
    url: proposedUrl,
  } = proposedFields;

  // Filter out segments that have no data on either side (e.g., dueDate when not set)
  const segments = allSegments.filter((seg) => {
    const currentVal = currentFields[seg.key];
    const proposedVal = proposedFields[seg.key];
    return currentVal != null || proposedVal != null;
  });
  const hasDescriptionSegment = segments.some(
    (segment) => segment.key === "description" && segment.inputType !== "html"
  );

  const [currentCollapsed, setCurrentCollapsed] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editingFields, setEditingFields] = useState<StructuredFields>({ ...proposedFields });
  const [fullEditorSegment, setFullEditorSegment] = useState<SegmentFieldConfig | null>(null);
  const getRewriteProposedText = useCallback(() => editingFields.description ?? "", [editingFields.description]);
  const handleApplyRewrite = useCallback((rewrittenText: string) => {
    setEditingFields((prev) => ({ ...prev, description: rewrittenText }));
  }, []);
  const {
    closeRewriteModal,
    isRewriteModalVisible,
    isRewriting,
    openRewriteModal,
    rewriteErrorMessage,
    submitRewrite,
  } = useAIRewriteSuggestion({
    currentText: currentFields.description,
    getProposedText: getRewriteProposedText,
    onApplyRewrite: handleApplyRewrite,
    title: editingFields.title,
  });

  // Reset editing state when proposed fields change (item switch)
  useEffect(() => {
    setIsEditing(false);
    setCurrentCollapsed(false);
    setEditingFields({
      title: proposedTitle,
      description: proposedDescription,
      dueDate: proposedDueDate,
      points: proposedPoints,
      url: proposedUrl,
    });
  }, [proposedTitle, proposedDescription, proposedDueDate, proposedPoints, proposedUrl]);

  // Auto-collapse current column when editing starts
  useEffect(() => {
    setCurrentCollapsed(isEditing);
  }, [isEditing]);

  useEffect(() => {
    if (!canEdit) {
      setIsEditing(false);
    }
  }, [canEdit]);

  const handleEdit = useCallback(() => {
    if (!canEdit) return;
    setEditingFields({ ...proposedFields });
    setIsEditing(true);
  }, [canEdit, proposedFields]);

  const handleSaveEdit = useCallback(() => {
    if (!canEdit) return;
    setIsEditing(false);
    onSaveEdit?.(editingFields);
  }, [canEdit, editingFields, onSaveEdit]);

  const handleCancelEdit = useCallback(() => {
    setIsEditing(false);
    setEditingFields({ ...proposedFields });
  }, [proposedFields]);

  const handleFieldChange = useCallback((key: keyof StructuredFields, value: string) => {
    setEditingFields((prev) => {
      if (key !== "points") {
        return {
          ...prev,
          [key]: value,
        };
      }

      const parsedPoints = parsePointsInput(value);
      if (parsedPoints === undefined) {
        return prev;
      }

      return {
        ...prev,
        points: parsedPoints,
      };
    });
  }, []);

  const handleOpenFullEditor = useCallback((segment: SegmentFieldConfig) => {
    setFullEditorSegment(segment);
  }, []);

  const handleCloseFullEditor = useCallback(() => {
    setFullEditorSegment(null);
  }, []);

  const handleSaveFullEditor = useCallback(
    (editedContent: string) => {
      if (!fullEditorSegment) return;
      const baseFields = isEditing ? editingFields : proposedFields;
      const editedFields = {
        ...baseFields,
        [fullEditorSegment.key]: editedContent,
      };
      setEditingFields(editedFields);
      if (!isEditing) {
        onSaveEdit?.(editedFields);
      }
    },
    [editingFields, fullEditorSegment, isEditing, onSaveEdit, proposedFields]
  );

  const fullEditorCurrentContent =
    fullEditorSegment && currentFields[fullEditorSegment.key] != null
      ? String(currentFields[fullEditorSegment.key])
      : "";
  const fullEditorProposedFields = isEditing ? editingFields : proposedFields;
  const fullEditorProposedContent =
    fullEditorSegment && fullEditorProposedFields[fullEditorSegment.key] != null
      ? String(fullEditorProposedFields[fullEditorSegment.key])
      : "";

  return (
    <View style={styles.container}>
      {/* Headers row */}
      <View
        style={[
          styles.headersRow,
          showTwoColumns && !currentCollapsed ? styles.headersRowDesktop : styles.headersRowMobile,
        ]}>
        {!currentCollapsed && (
          <View style={[styles.currentHeader, showTwoColumns && styles.headerHalf]}>
            <CustomText
              text="Current"
              useTranslationText={false}
              size="s"
              weight="ultraBold"
              style={styles.currentHeaderText}
            />
            <Pressable
              accessibilityRole="button"
              accessibilityLabel="Collapse current"
              onPress={() => setCurrentCollapsed(true)}
              style={styles.iconButton}>
              <CaretLeftIcon size={14} color={colors.grey[500]} weight="bold" />
            </Pressable>
          </View>
        )}
        <View style={[styles.proposedHeader, !currentCollapsed && showTwoColumns && styles.headerHalf]}>
          <View style={styles.proposedHeaderLeft}>
            {currentCollapsed && (
              <Pressable
                accessibilityRole="button"
                accessibilityLabel="Expand current"
                onPress={() => setCurrentCollapsed(false)}
                style={styles.iconButtonSuccess}>
                <CaretRightIcon size={14} color={colors.success[700]} weight="bold" />
              </Pressable>
            )}
            <CustomText
              text="Proposed"
              useTranslationText={false}
              size="s"
              weight="ultraBold"
              style={styles.proposedHeaderText}
            />
          </View>
          {isEditing ? (
            <View style={styles.editingActions}>
              <Pressable onPress={handleCancelEdit} style={styles.cancelButton}>
                <XIcon size={12} color={colors.grey[600]} weight="bold" />
                <CustomText text="Cancel" useTranslationText={false} size="xs" style={styles.cancelButtonText} />
              </Pressable>
              {hasDescriptionSegment && <AIRewriteActionButton disabled={isRewriting} onPress={openRewriteModal} />}
              <Pressable onPress={handleSaveEdit} style={styles.saveButton}>
                <CheckIcon size={12} color={colors.white} weight="bold" />
                <CustomText text="Save" useTranslationText={false} size="xs" style={styles.saveButtonText} />
              </Pressable>
            </View>
          ) : (
            <Pressable
              accessibilityRole="button"
              accessibilityLabel="Edit proposed changes"
              accessibilityState={{ disabled: !canEdit }}
              disabled={!canEdit}
              onPress={handleEdit}
              style={[styles.editButton, !canEdit && styles.editButtonDisabled]}>
              <PencilSimpleIcon size={14} color={canEdit ? colors.grey[700] : colors.grey[400]} weight="regular" />
              <CustomText
                text="Edit"
                useTranslationText={false}
                size="xs"
                style={[styles.editButtonText, !canEdit && styles.editButtonTextDisabled]}
              />
            </Pressable>
          )}
        </View>
      </View>

      {/* Segment rows */}
      {segments.map((seg, index) => {
        const proposedValue = (isEditing ? editingFields : proposedFields)[seg.key];
        const currentValue = currentFields[seg.key];
        const proposedDisplay = proposedValue != null ? String(proposedValue) : "";
        const currentDisplay = currentValue != null ? String(currentValue) : "";
        const hasChanged = currentDisplay !== proposedDisplay;
        const isLast = index === segments.length - 1;

        return (
          <View
            key={seg.key}
            style={[
              styles.segmentRow,
              showTwoColumns && !currentCollapsed ? styles.segmentRowDesktop : styles.segmentRowMobile,
              !isLast && styles.segmentRowBorder,
            ]}>
            {/* Current field */}
            {!currentCollapsed && (
              <View
                style={[
                  styles.fieldBlock,
                  showTwoColumns && styles.fieldBlockHalf,
                  showTwoColumns && styles.fieldBlockCurrentBorder,
                ]}>
                <View style={styles.fieldLabelRow}>
                  <CustomText
                    text={seg.label}
                    useTranslationText={false}
                    size="xs"
                    weight="bold"
                    style={styles.fieldLabel}
                  />
                </View>
                {seg.inputType === "html" ? (
                  <HtmlField value={currentDisplay} />
                ) : seg.inputType === "textarea" && currentDisplay ? (
                  <CustomHtml html={currentDisplay} style={richContentBaseStyle} />
                ) : (
                  <CustomText
                    text={currentDisplay || "Not set"}
                    useTranslationText={false}
                    size="s"
                    style={currentDisplay ? styles.fieldValue : styles.fieldValueEmpty}
                  />
                )}
              </View>
            )}

            {/* Proposed field */}
            <View
              style={[
                styles.fieldBlock,
                !currentCollapsed && showTwoColumns && styles.fieldBlockHalf,
                hasChanged && !isEditing && styles.fieldBlockChanged,
              ]}>
              <View style={styles.fieldLabelRow}>
                <CustomText
                  text={seg.label}
                  useTranslationText={false}
                  size="xs"
                  weight="bold"
                  style={styles.proposedFieldLabel}
                />
                {!isEditing && hasChanged && (
                  <View style={styles.changedBadge}>
                    <CustomText
                      text="Changed"
                      useTranslationText={false}
                      size="xs"
                      weight="medium"
                      style={styles.changedBadgeText}
                    />
                  </View>
                )}
                {!isEditing && !hasChanged && currentDisplay !== "" && (
                  <CustomText text="No change" useTranslationText={false} size="xs" style={styles.noChangeBadgeText} />
                )}
                {seg.inputType === "textarea" && (
                  <Pressable
                    accessibilityRole="button"
                    accessibilityLabel={`Open full editor for ${seg.label.toLowerCase()}`}
                    onPress={() => handleOpenFullEditor(seg)}
                    style={styles.fullEditorIconButton}>
                    <ArrowsOutIcon size={15} color="#475467" weight="regular" />
                  </Pressable>
                )}
              </View>
              {isEditing ? (
                <FieldInput config={seg} value={proposedDisplay} onChange={(v) => handleFieldChange(seg.key, v)} />
              ) : seg.inputType === "html" ? (
                <HtmlField value={proposedDisplay} />
              ) : seg.inputType === "textarea" && proposedDisplay ? (
                <CustomHtml html={proposedDisplay} style={richContentBaseStyle} />
              ) : (
                <CustomText
                  text={proposedDisplay || "Not set"}
                  useTranslationText={false}
                  size="s"
                  style={proposedDisplay ? styles.fieldValue : styles.fieldValueEmpty}
                />
              )}
            </View>
          </View>
        );
      })}
      <AIRewriteModal
        errorMessage={rewriteErrorMessage}
        isRewriting={isRewriting}
        onCancel={closeRewriteModal}
        onRewrite={submitRewrite}
        visible={isRewriteModalVisible}
      />
      <FullContentEditorModal
        currentContent={fullEditorCurrentContent}
        proposedContent={fullEditorProposedContent}
        title={fullEditorSegment?.label ?? "Content"}
        visible={Boolean(fullEditorSegment)}
        isEditDisabled={isEditDisabled}
        onClose={handleCloseFullEditor}
        onSave={canEdit ? handleSaveFullEditor : undefined}
      />
    </View>
  );
}

// ---------------------------------------------------------------------------
// HTML Field with Source / Preview toggle
// ---------------------------------------------------------------------------

interface HtmlFieldProps {
  value: string;
  editable?: boolean;
  onChange?: (v: string) => void;
  placeholder?: string;
}

function HtmlField({ value, editable, onChange, placeholder }: HtmlFieldProps) {
  const [showPreview, setShowPreview] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);

  // Strip <script> tags before rendering in the iframe so no untrusted code
  // executes. Image src/href attributes and all other markup are preserved.
  const sanitizedForPreview = value
    ? value.replace(/<script\b[^>]*>[\s\S]*?<\/script>/gi, "").replace(/<script\b[^>]*\/>/gi, "")
    : "";
  const iframeContent = sanitizedForPreview || "<p style='color:#9ca3af;font-family:sans-serif'>No content</p>";

  const renderToggleRow = (fullscreen: boolean) => (
    <View style={styles.htmlToggleRowWithExpand}>
      <View style={styles.htmlToggleRow}>
        <Pressable
          onPress={() => setShowPreview(false)}
          style={[styles.htmlToggleBtn, !showPreview && styles.htmlToggleBtnActive]}>
          <CustomText
            text="Source"
            useTranslationText={false}
            size="xs"
            weight={!showPreview ? "bold" : "normal"}
            style={!showPreview ? styles.htmlToggleLabelActive : styles.htmlToggleLabel}
          />
        </Pressable>
        <Pressable
          onPress={() => setShowPreview(true)}
          style={[styles.htmlToggleBtn, showPreview && styles.htmlToggleBtnActive]}>
          <CustomText
            text="Preview"
            useTranslationText={false}
            size="xs"
            weight={showPreview ? "bold" : "normal"}
            style={showPreview ? styles.htmlToggleLabelActive : styles.htmlToggleLabel}
          />
        </Pressable>
      </View>
      <Pressable
        onPress={() => setIsFullscreen(!fullscreen)}
        style={styles.fullscreenToggle}
        accessibilityLabel={fullscreen ? "Exit fullscreen" : "Expand to fullscreen"}>
        {fullscreen ? (
          <ArrowsInIcon size={15} color={colors.grey[500]} />
        ) : (
          <ArrowsOutIcon size={15} color={colors.grey[500]} />
        )}
      </Pressable>
    </View>
  );

  const renderBody = (fullscreen: boolean) =>
    showPreview ? (
      <View style={fullscreen ? styles.htmlPreviewContainerFullscreen : undefined}>
        <View style={styles.htmlPreviewNote}>
          <CustomText
            text="⚠ Some images and media hosted in Brightspace may not display in this preview but will work correctly in Brightspace."
            useTranslationText={false}
            size="xs"
            style={styles.htmlPreviewNoteText}
          />
        </View>
        {createElement("iframe", {
          srcDoc: iframeContent,
          sandbox: "allow-popups",
          style: {
            width: "100%",
            height: fullscreen ? "100%" : undefined,
            minHeight: fullscreen ? 0 : 320,
            flex: fullscreen ? 1 : undefined,
            border: "1px solid #e5e7eb",
            borderRadius: 8,
            background: "white",
            display: "block",
          },
        })}
      </View>
    ) : editable ? (
      <TextInput
        value={value}
        onChangeText={onChange}
        multiline
        style={[styles.htmlInput, fullscreen && styles.htmlInputFullscreen]}
        textAlignVertical="top"
        placeholder={placeholder ?? "Paste or edit HTML here\u2026"}
        placeholderTextColor={colors.grey[500]}
      />
    ) : (
      <TextInput
        value={value || "Not set"}
        editable={false}
        multiline
        style={[styles.htmlInputReadOnly, fullscreen && styles.htmlInputFullscreen]}
        textAlignVertical="top"
      />
    );

  return (
    <View>
      {renderToggleRow(false)}
      {renderBody(false)}

      {/* Fullscreen modal */}
      <Modal visible={isFullscreen} animationType="fade" onRequestClose={() => setIsFullscreen(false)}>
        <SafeAreaView style={styles.fullscreenContainer}>
          <View style={styles.fullscreenHeader}>
            <CustomText
              text="HTML BODY"
              useTranslationText={false}
              size="s"
              weight="ultraBold"
              style={styles.fullscreenTitle}
            />
            <Pressable
              onPress={() => setIsFullscreen(false)}
              style={styles.fullscreenCloseBtn}
              accessibilityLabel="Exit fullscreen">
              <XIcon size={16} color={colors.grey[600]} />
            </Pressable>
          </View>
          <View style={styles.fullscreenBody}>
            {renderToggleRow(true)}
            {renderBody(true)}
          </View>
        </SafeAreaView>
      </Modal>
    </View>
  );
}

// ---------------------------------------------------------------------------
// Field Input
// ---------------------------------------------------------------------------

interface FieldInputProps {
  config: SegmentFieldConfig;
  value: string;
  onChange: (value: string) => void;
}

function FieldInput({ config, value, onChange }: FieldInputProps) {
  const isMultiline = config.inputType === "textarea";
  const isHtml = config.inputType === "html";
  const isNumber = config.inputType === "number";
  const isDate = config.inputType === "date";

  if (isDate) {
    // Parse the locale string ("3/21/2026") back to a Date for the picker,
    // and convert the picker's Date output back to a locale string.
    const dateValue = value ? new Date(value) : undefined;
    const validDateValue = dateValue && !Number.isNaN(dateValue.getTime()) ? dateValue : undefined;

    return (
      <DatePickerInput
        locale="en"
        value={validDateValue}
        onChange={(d) => onChange(d ? d.toLocaleDateString("en-US") : "")}
        inputMode="start"
        mode="outlined"
        outlineColor={colors.grey[300]}
        activeOutlineColor={colors.brand[600]}
        selectionColor={false}
        withDateFormatInLabel={false}
        placeholder="MM/DD/YYYY"
        inputProps={{ color: colors.grey[900], mode: "outline" }}
        theme={{ roundness: 8 }}
        style={styles.dateInput}
      />
    );
  }

  if (isHtml) {
    return <HtmlField value={value} editable onChange={onChange} />;
  }

  return (
    <TextInput
      value={value}
      onChangeText={onChange}
      multiline={isMultiline}
      keyboardType={isNumber ? "numeric" : config.inputType === "url" ? "url" : "default"}
      style={[styles.textInput, isMultiline && styles.textInputMultiline, isNumber && styles.textInputNumber]}
      textAlignVertical={isMultiline ? "top" : "center"}
      placeholder={`Enter ${config.label.toLowerCase()}`}
      placeholderTextColor={colors.grey[500]}
    />
  );
}

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------

const richContentBaseStyle = {
  fontSize: 14,
  lineHeight: 20,
  color: "#344054",
};

const styles = StyleSheet.create({
  container: {
    backgroundColor: colors.white,
    borderWidth: 1,
    borderColor: colors.grey[200],
    borderRadius: 16,
    overflow: "hidden",
  },
  headersRow: {},
  headersRowDesktop: {
    flexDirection: "row",
    borderBottomWidth: 1,
    borderBottomColor: colors.grey[200],
  },
  headersRowMobile: {
    flexDirection: "column",
    borderBottomWidth: 1,
    borderBottomColor: colors.grey[200],
  },
  headerHalf: {
    flex: 1,
  },
  currentHeader: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    paddingHorizontal: 16,
    height: 40,
    borderRightWidth: 1,
    borderRightColor: colors.grey[200],
  },
  currentHeaderText: {
    color: colors.grey[700],
  },
  proposedHeader: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    paddingHorizontal: 16,
    height: 40,
    backgroundColor: "#F3FEE7",
  },
  proposedHeaderLeft: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
  },
  proposedHeaderText: {
    color: colors.success[700],
  },
  iconButton: {
    padding: 4,
    borderRadius: 4,
  },
  iconButtonSuccess: {
    padding: 4,
    borderRadius: 4,
  },
  editButton: {
    flexDirection: "row",
    alignItems: "center",
    gap: 4,
    paddingHorizontal: 10,
    paddingVertical: 4,
    borderRadius: 6,
    borderWidth: 1,
    borderColor: colors.grey[200],
    backgroundColor: colors.white,
  },
  editButtonText: {
    color: colors.grey[700],
  },
  editButtonDisabled: {
    backgroundColor: colors.grey[50],
    borderColor: colors.grey[200],
    opacity: 0.7,
  },
  editButtonTextDisabled: {
    color: colors.grey[400],
  },
  editingActions: {
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
  },
  cancelButton: {
    flexDirection: "row",
    alignItems: "center",
    gap: 4,
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 6,
  },
  cancelButtonText: {
    color: colors.grey[600],
  },
  saveButton: {
    flexDirection: "row",
    alignItems: "center",
    gap: 4,
    paddingHorizontal: 10,
    paddingVertical: 4,
    borderRadius: 6,
    backgroundColor: colors.brand[600],
  },
  saveButtonText: {
    color: colors.white,
  },
  // --- Segment rows ---
  segmentRow: {},
  segmentRowDesktop: {
    flexDirection: "row",
  },
  segmentRowMobile: {
    flexDirection: "column",
  },
  segmentRowBorder: {
    borderBottomWidth: 1,
    borderBottomColor: colors.grey[200],
  },
  fieldBlock: {
    flex: 1,
    paddingHorizontal: 20,
    paddingVertical: 20,
  },
  fieldBlockHalf: {
    flex: 1,
  },
  fieldBlockCurrentBorder: {
    borderRightWidth: 1,
    borderRightColor: colors.grey[200],
  },
  fieldBlockChanged: {
    backgroundColor: "rgba(240, 253, 244, 0.3)",
  },
  fieldLabelRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
    marginBottom: 6,
  },
  fieldLabel: {
    color: colors.grey[400],
    fontSize: 11,
    letterSpacing: 0.5,
  },
  proposedFieldLabel: {
    color: colors.grey[400],
    fontSize: 11,
    letterSpacing: 0.5,
  },
  changedBadge: {
    paddingHorizontal: 6,
    paddingVertical: 2,
    borderRadius: 4,
    backgroundColor: "#F3FEE7",
  },
  changedBadgeText: {
    color: "#3B7C0F",
    fontSize: 10,
  },
  noChangeBadgeText: {
    color: colors.grey[500],
    fontSize: 10,
  },
  fullEditorIconButton: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    marginLeft: "auto",
    width: 24,
    height: 24,
    borderRadius: 6,
  },
  fieldValue: {
    color: colors.grey[900],
    lineHeight: 22,
  },
  fieldValueEmpty: {
    color: colors.grey[400],
    lineHeight: 22,
    fontStyle: "italic",
  },
  htmlInput: {
    minHeight: 320,
    padding: 12,
    fontSize: 12,
    lineHeight: 18,
    color: colors.grey[900],
    backgroundColor: colors.grey[50],
    borderWidth: 1,
    borderColor: colors.grey[300],
    borderRadius: 8,
    fontFamily: "monospace",
  },
  htmlInputReadOnly: {
    minHeight: 200,
    padding: 12,
    fontSize: 12,
    lineHeight: 18,
    color: colors.grey[700],
    backgroundColor: colors.grey[50],
    borderWidth: 1,
    borderColor: colors.grey[200],
    borderRadius: 8,
    fontFamily: "monospace",
  },
  htmlToggleRow: {
    flexDirection: "row" as const,
    marginBottom: 6,
    borderWidth: 1,
    borderColor: colors.grey[200],
    borderRadius: 6,
    overflow: "hidden",
    alignSelf: "flex-start" as const,
  },
  htmlToggleBtn: {
    paddingHorizontal: 12,
    paddingVertical: 5,
    backgroundColor: colors.white,
  },
  htmlToggleBtnActive: {
    backgroundColor: colors.grey[100],
  },
  htmlToggleLabel: {
    color: colors.grey[500],
  },
  htmlToggleLabelActive: {
    color: colors.grey[800],
  },
  htmlToggleRowWithExpand: {
    flexDirection: "row" as const,
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: 6,
  },
  fullscreenToggle: {
    padding: 4,
    borderRadius: 4,
  },
  fullscreenContainer: {
    flex: 1,
    backgroundColor: colors.white,
  },
  fullscreenHeader: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    paddingHorizontal: 16,
    paddingVertical: 10,
    borderBottomWidth: 1,
    borderBottomColor: colors.grey[200],
    backgroundColor: colors.white,
  },
  fullscreenTitle: {
    color: colors.grey[700],
    fontSize: 11,
    letterSpacing: 0.5,
  },
  fullscreenCloseBtn: {
    padding: 6,
    borderRadius: 6,
    borderWidth: 1,
    borderColor: colors.grey[200],
  },
  fullscreenBody: {
    flex: 1,
    padding: 16,
    gap: 0,
  },
  htmlInputFullscreen: {
    flex: 1,
    minHeight: 0,
    maxHeight: undefined,
  },
  htmlPreviewContainerFullscreen: {
    flex: 1,
    gap: 6,
  },
  htmlPreviewNote: {
    marginBottom: 6,
    paddingHorizontal: 10,
    paddingVertical: 6,
    backgroundColor: "#FFFBEB",
    borderWidth: 1,
    borderColor: "#FDE68A",
    borderRadius: 6,
  },
  htmlPreviewNoteText: {
    color: "#92400E",
  },
  textInput: {
    padding: 10,
    fontSize: 14,
    lineHeight: 22,
    color: colors.grey[900],
    backgroundColor: colors.white,
    borderWidth: 1,
    borderColor: colors.grey[300],
    borderRadius: 8,
    fontFamily: "Inter",
  },
  textInputMultiline: {
    minHeight: 200,
    textAlignVertical: "top",
  },
  textInputNumber: {
    width: 128,
  },
  dateInput: {
    backgroundColor: colors.white,
  },
});

export default SegmentedDiffView;
